/*
 * Lemonade Training Oy copy rights all right reserved.
 * Request/response contracts for editing, deleting and restoring an athlete's
 * own results, with the parsers the callables validate against.
 *
 * The parsers return a result instead of throwing so plain-JS Cloud Functions
 * can map a rejection to `invalid-argument` without an error class crossing
 * the package boundary:
 *   const parsed = parseDeleteMovementResultRequest(request.data);
 *   if (!parsed.ok) throw new HttpsError("invalid-argument", parsed.message);
 * Unknown keys are rejected, never ignored: a request shape that drifts must
 * fail loudly on the first call. Omit a key to leave it out: the callable SDK
 * sends a key holding `undefined` as `null`, which every field rejects.
 */

import { RESULT_EVENT_SOURCES, ResultEventSource } from "./resultEvents";

/**
 * Server-enforced cap on result notes, in UTF-16 code units after trim (the
 * unit `maxLength` counts on web and mobile).
 */
export const MOVEMENT_RESULT_NOTES_MAX_LENGTH = 500;

/** Firestore's document id limit. */
export const RECORD_ID_MAX_LENGTH = 1500;

/**
 * Server-enforced upper bounds on logged values. `time` is in seconds; weight,
 * reps and sets match the XP engine's clamps.
 */
export const MOVEMENT_RESULT_MAX = {
  weight: 1000,
  reps: 1000,
  sets: 100,
  time: 86_400,
  calories: 10_000,
  distance: 100_000,
} as const;

/** A soft-deleted result can be restored while younger than this. */
export const MOVEMENT_RESULT_RESTORE_WINDOW_DAYS = 30;

export const MOVEMENT_RESULT_RESTORE_WINDOW_MS =
  MOVEMENT_RESULT_RESTORE_WINDOW_DAYS * 24 * 60 * 60 * 1000;

/** How far past the server clock a result date may be (covers time zones). */
export const MOVEMENT_RESULT_DATE_FUTURE_TOLERANCE_MS = 24 * 60 * 60 * 1000;

/** Earliest accepted result date: 1900-01-01T00:00:00Z. */
const MOVEMENT_RESULT_DATE_MIN_MS = Date.UTC(1900, 0, 1);

const ISO_DATE_TIME =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(?:Z|([+-])(\d{2}):(\d{2}))$/;

/**
 * Epoch ms of a zoned ISO-8601 date-time, or NaN. Unlike `Date.parse`, a field
 * out of range (Feb 30, 24:00) is rejected instead of rolled over.
 */
function isoDateTimeToMs(raw: unknown): number {
  const m = typeof raw === "string" ? ISO_DATE_TIME.exec(raw) : null;
  if (!m) return NaN;
  const [y, mo, d, h, mi] = [1, 2, 3, 4, 5].map((i) => Number(m[i]));
  const s = Number(m[6] ?? "0");
  const ms = Number((m[7] ?? "0").padEnd(3, "0"));
  // setUTCFullYear, unlike Date.UTC, does not map years 0-99 to 1900-1999.
  const local = new Date(0);
  local.setUTCFullYear(y, mo - 1, d);
  local.setUTCHours(h, mi, s, ms);
  if (
    local.getUTCFullYear() !== y ||
    local.getUTCMonth() !== mo - 1 ||
    local.getUTCDate() !== d ||
    local.getUTCHours() !== h ||
    local.getUTCMinutes() !== mi ||
    local.getUTCSeconds() !== s
  ) {
    return NaN;
  }
  if (m[8] === undefined) return local.getTime();
  const offsetH = Number(m[9]);
  const offsetM = Number(m[10]);
  if (offsetH > 23 || offsetM > 59) return NaN;
  const sign = m[8] === "+" ? 1 : -1;
  return local.getTime() - sign * (offsetH * 60 + offsetM) * 60_000;
}

export type ParseResult<T> =
  { ok: true; value: T } | { ok: false; field: string; message: string };

/**
 * A result date: an ISO-8601 date-time with an explicit zone, from 1900 up to
 * `nowMs` plus {@link MOVEMENT_RESULT_DATE_FUTURE_TOLERANCE_MS}. Normalised to
 * `toISOString()`, the one stored format.
 */
export function parseResultDate(
  raw: unknown,
  nowMs: number,
): ParseResult<string> {
  const parsed = isoDateTimeToMs(raw);
  if (!Number.isFinite(parsed)) {
    return fail("date", "date must be a valid ISO string.");
  }
  if (parsed < MOVEMENT_RESULT_DATE_MIN_MS) {
    return fail("date", "date must not be before 1900.");
  }
  if (parsed > nowMs + MOVEMENT_RESULT_DATE_FUTURE_TOLERANCE_MS) {
    return fail("date", "date must not be more than 1 day in the future.");
  }
  return { ok: true, value: new Date(parsed).toISOString() };
}

/**
 * Soft-delete one of the caller's own results, or restore it with
 * `restore: true` (the Undo). One code path, so the two cannot diverge. There
 * is no target-uid field: results are only ever edited by their owner.
 */
export type DeleteMovementResultRequest = {
  /** Document id under `Users/{caller uid}/Records_1RM`. */
  recordId: string;
  restore?: boolean;
  /**
   * The calling app, for the server's result event; absent logs "unknown".
   * A server that predates it rejects the request as an unknown field.
   */
  source?: ResultEventSource;
};

export type DeleteMovementResultResponse = {
  success: boolean;
  recordId: string;
  restored: boolean;
  /** True when the change moved which record is `current` for that movement. */
  currentChanged?: boolean;
};

/**
 * Edit one of the caller's own results: value fields, `date` and `notes`.
 * `kind` and `movementRegistryID` are immutable and not expressible here; a
 * mis-logged movement or kind is corrected by delete and re-log.
 * An absent value field — or one equal to the stored value — keeps value and
 * e1RM exactly as stored; send only fields that changed.
 */
export type UpdateMovementResultRequest = {
  recordId: string;
  weight?: number;
  reps?: number;
  sets?: number;
  time?: number;
  calories?: number;
  distance?: number;
  unlocked?: boolean;
  /** Same rule as a logged result: see {@link parseResultDate}. */
  date?: string;
  /**
   * Absent leaves the note unchanged, a string replaces it, and an empty or
   * whitespace-only string clears it. `null` is rejected: the SDK produces it
   * from `undefined`, so accepting it would erase notes by accident.
   */
  notes?: string;
  /**
   * The calling app, for the server's result event; never an edit on its own.
   * A server that predates it rejects the request as an unknown field.
   */
  source?: ResultEventSource;
};

/** What the parser hands the callable: `notes: null` means clear. */
export type ParsedUpdateMovementResultRequest = Omit<
  UpdateMovementResultRequest,
  "notes"
> & { notes?: string | null };

export type UpdateMovementResultResponse = {
  success: boolean;
  recordId: string;
  /** The edited record's e1RM, when its measure has one (as on logging). */
  estimated1RM?: number;
  currentChanged?: boolean;
};

export const DELETE_MOVEMENT_RESULT_REQUEST_KEYS = [
  "recordId",
  "restore",
  "source",
] as const satisfies readonly (keyof DeleteMovementResultRequest)[];

const UPDATE_NUMBER_KEYS = [
  "weight",
  "reps",
  "sets",
  "time",
  "calories",
  "distance",
] as const;

const UPDATE_INTEGER_KEYS: readonly string[] = ["reps", "sets"];

// Zero is a valid set count but never a valid lift, rep count, time or effort.
const UPDATE_ZERO_ALLOWED_KEYS: readonly string[] = ["sets"];

const UPDATE_EDIT_KEYS = [
  ...UPDATE_NUMBER_KEYS,
  "unlocked",
  "date",
  "notes",
] as const;

export const UPDATE_MOVEMENT_RESULT_REQUEST_KEYS = [
  "recordId",
  ...UPDATE_EDIT_KEYS,
  "source",
] as const satisfies readonly (keyof UpdateMovementResultRequest)[];

/** UTF-8 length, or -1 for a lone surrogate (not encodable). */
function utf8ByteLength(s: string): number {
  let bytes = 0;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 0x80) bytes += 1;
    else if (c < 0x800) bytes += 2;
    else if (c >= 0xd800 && c <= 0xdbff) {
      const next = s.charCodeAt(i + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return -1;
      bytes += 4;
      i += 1;
    } else if (c >= 0xdc00 && c <= 0xdfff) return -1;
    else bytes += 3;
  }
  return bytes;
}

/**
 * A usable Firestore document id: 1..1500 UTF-8 bytes, no "/" or control
 * characters, not "." or "..", not reserved `__x__`.
 */
export function isSafeRecordId(obj: unknown): obj is string {
  if (typeof obj !== "string" || obj === "." || obj === "..") return false;
  const bytes = utf8ByteLength(obj);
  return (
    bytes > 0 &&
    bytes <= RECORD_ID_MAX_LENGTH &&
    !/[/\u0000-\u001f\u007f]/.test(obj) &&
    !/^__[\s\S]*__$/.test(obj)
  );
}

/**
 * A string is trimmed; empty or whitespace-only becomes `null` (no note), and
 * longer than {@link MOVEMENT_RESULT_NOTES_MAX_LENGTH} is rejected, never
 * truncated. Anything else, `null` included, is rejected. Stored raw after
 * trim: a consumer rendering or exporting it escapes for its own sink.
 */
export function parseResultNotes(raw: unknown): ParseResult<string | null> {
  if (typeof raw !== "string") return fail("notes", "notes must be a string.");
  const trimmed = raw.trim();
  if (trimmed.length > MOVEMENT_RESULT_NOTES_MAX_LENGTH) {
    return fail(
      "notes",
      `notes must be at most ${MOVEMENT_RESULT_NOTES_MAX_LENGTH} characters.`,
    );
  }
  return { ok: true, value: trimmed === "" ? null : trimmed };
}

/** The calling app: one of {@link RESULT_EVENT_SOURCES}. */
export function parseResultSource(
  raw: unknown,
): ParseResult<ResultEventSource> {
  if (!(RESULT_EVENT_SOURCES as readonly unknown[]).includes(raw)) {
    return fail(
      "source",
      `source must be one of: ${RESULT_EVENT_SOURCES.join(", ")}.`,
    );
  }
  return { ok: true, value: raw as ResultEventSource };
}

function hasOwn(obj: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

function fail<T>(field: string, message: string): ParseResult<T> {
  return { ok: false, field, message };
}

/** A callable request must be a plain object with no key outside `allowed`. */
export function checkRequestKeys(
  raw: unknown,
  allowed: readonly string[],
): ParseResult<Record<string, unknown>> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return fail("request", "request must be an object.");
  }
  // A wire `__proto__` key becomes the decoded object's prototype, which would
  // hide every field from the unknown-key check below.
  const proto = Object.getPrototypeOf(raw);
  if (proto !== Object.prototype && proto !== null) {
    return fail("request", "request must be a plain object.");
  }
  const data = raw as Record<string, unknown>;
  for (const key of Object.keys(data)) {
    if (!allowed.includes(key)) {
      const shown = key.length > 64 ? `${key.slice(0, 64)}…` : key;
      return fail(shown, `unexpected field "${shown}".`);
    }
  }
  return { ok: true, value: data };
}

function checkShape(
  raw: unknown,
  allowed: readonly string[],
): ParseResult<Record<string, unknown>> {
  const shape = checkRequestKeys(raw, allowed);
  if (!shape.ok) return shape;
  const data = shape.value;
  if (!hasOwn(data, "recordId") || !isSafeRecordId(data.recordId)) {
    return fail("recordId", "recordId must be a valid record id.");
  }
  return { ok: true, value: data };
}

export function parseDeleteMovementResultRequest(
  raw: unknown,
): ParseResult<DeleteMovementResultRequest> {
  const shape = checkShape(raw, DELETE_MOVEMENT_RESULT_REQUEST_KEYS);
  if (!shape.ok) return shape;
  const data = shape.value;
  const value: DeleteMovementResultRequest = {
    recordId: data.recordId as string,
  };
  if (hasOwn(data, "restore")) {
    if (typeof data.restore !== "boolean") {
      return fail("restore", "restore must be a boolean.");
    }
    value.restore = data.restore;
  }
  if (hasOwn(data, "source")) {
    const source = parseResultSource(data.source);
    if (!source.ok) return source;
    value.source = source.value;
  }
  return { ok: true, value };
}

/** `nowMs` is the server clock the date bound is checked against. */
export function parseUpdateMovementResultRequest(
  raw: unknown,
  nowMs: number,
): ParseResult<ParsedUpdateMovementResultRequest> {
  if (!Number.isFinite(nowMs)) {
    throw new TypeError("parseUpdateMovementResultRequest needs nowMs.");
  }
  const shape = checkShape(raw, UPDATE_MOVEMENT_RESULT_REQUEST_KEYS);
  if (!shape.ok) return shape;
  const data = shape.value;
  const value: ParsedUpdateMovementResultRequest = {
    recordId: data.recordId as string,
  };

  for (const key of UPDATE_NUMBER_KEYS) {
    if (!hasOwn(data, key)) continue;
    const n = data[key];
    if (typeof n !== "number" || !Number.isFinite(n) || n < 0) {
      return fail(key, `${key} must be a non-negative number.`);
    }
    if (n === 0 && !UPDATE_ZERO_ALLOWED_KEYS.includes(key)) {
      return fail(key, `${key} must be greater than zero.`);
    }
    if (UPDATE_INTEGER_KEYS.includes(key) && !Number.isInteger(n)) {
      return fail(key, `${key} must be a whole number.`);
    }
    if (n > MOVEMENT_RESULT_MAX[key]) {
      return fail(key, `${key} must be at most ${MOVEMENT_RESULT_MAX[key]}.`);
    }
    value[key] = n;
  }

  if (hasOwn(data, "unlocked")) {
    if (typeof data.unlocked !== "boolean") {
      return fail("unlocked", "unlocked must be a boolean.");
    }
    value.unlocked = data.unlocked;
  }

  if (hasOwn(data, "date")) {
    const date = parseResultDate(data.date, nowMs);
    if (!date.ok) return date;
    value.date = date.value;
  }

  if (hasOwn(data, "notes")) {
    const notes = parseResultNotes(data.notes);
    if (!notes.ok) return notes;
    value.notes = notes.value;
  }

  if (hasOwn(data, "source")) {
    const source = parseResultSource(data.source);
    if (!source.ok) return source;
    value.source = source.value;
  }

  if (!UPDATE_EDIT_KEYS.some((key) => hasOwn(value, key))) {
    return fail("request", "nothing to update.");
  }
  return { ok: true, value };
}
